/**
 * Data manager for word-view-page
 * Handles data loading, processing, filtering, and pagination
 */

import { gql } from '@apollo/client'
import { apolloClient } from './apollo.js'

// Type definitions
export interface Language {
  id: string
  name: string
}

export interface MeaningType {
  id: string
  name: string
}

export interface Spelling {
  id: string
  spelling: string
  system: { id: string; name: string }
  meaning: { id: string; language: { id: string; name: string }; word: { id: string }; type: { id: string; name: string } }
}

export interface SpellingSystem {
  id: string
  name: string
}

export interface SpellingsData {
  spellings: Spelling[]
}

export interface LanguagesData {
  languages: Language[]
}

export interface MeaningTypesData {
  meaningTypes: MeaningType[]
}

/** One word with its spellings, for grouping. */
export type WordGroup = { wordId: string; spellings: Spelling[] }

/** Column configuration: language, selected meaning types, and spelling systems */
export type ColumnConfig = {
  languageId: string
  meaningTypeIds: string[] // empty array means "all meaning types"
  spellingSystemIds: string[] // empty array means "all spelling systems"
}

export type FilterMode = 'includes' | 'equals'

/** State change callback type */
export type StateChangeCallback = (state: {
  loading: boolean
  processing: boolean
  error: string
}) => void

/**
 * Data manager for word view page
 * Handles all data operations: loading, processing, filtering, pagination
 */
export class WordViewDataManager {
  // Data
  private headwords: Spelling[] = []
  private languages: Language[] = []
  private meaningTypes: MeaningType[] = []
  
  // State
  private loading = true
  private processing = false
  private error = ''
  
  // Filtering and pagination
  private filter = ''
  private filterMode: FilterMode = 'includes'
  private currentPage = 1
  private itemsPerPage = 50
  private totalCount = 0 // Total count from server (spellings count, used for estimation)
  
  // Performance optimization: Cache computed values
  private _groupedCache: WordGroup[] | null = null
  private _groupedCacheKey: string = ''
  private _spellingSystemsCache: Map<string, SpellingSystem[]> = new Map()
  
  // Processing state for async operations
  private _processingGrouped: boolean = false
  private _loadingSpellings: boolean = false
  
  // State change callback
  private stateChangeCallback: StateChangeCallback | null = null
  
  constructor() {}
  
  /** Set callback for state changes */
  setStateChangeCallback(callback: StateChangeCallback) {
    this.stateChangeCallback = callback
  }
  
  /** Notify state change */
  private notifyStateChange() {
    if (this.stateChangeCallback) {
      this.stateChangeCallback({
        loading: this.loading,
        processing: this.processing,
        error: this.error,
      })
    }
  }
  
  // Getters
  getHeadwords(): Spelling[] {
    return this.headwords
  }
  
  getLanguages(): Language[] {
    return this.languages
  }
  
  getMeaningTypes(): MeaningType[] {
    return this.meaningTypes
  }
  
  getTotalCount(): number {
    return this.totalCount
  }
  
  getLoading(): boolean {
    return this.loading
  }
  
  getProcessing(): boolean {
    return this.processing
  }
  
  getError(): string {
    return this.error
  }
  
  getFilter(): string {
    return this.filter
  }

  getFilterMode(): FilterMode {
    return this.filterMode
  }
  
  setFilter(filter: string) {
    this.filter = filter
    this.currentPage = 1
    // Clear filtered cache when filter changes
    // Reload data when filter changes (server-side filtering)
    this.loadSpellings()
  }

  setFilterMode(mode: FilterMode) {
    if (this.filterMode === mode) return
    this.filterMode = mode
    this.currentPage = 1
  }
  
  getCurrentPage(): number {
    return this.currentPage
  }
  
  setCurrentPage(page: number) {
    this.currentPage = page
    // Clear paginated cache when page changes
    // Reload data when page changes (server-side pagination)
    this.loadSpellings()
  }
  
  getItemsPerPage(): number {
    return this.itemsPerPage
  }
  
  setItemsPerPage(items: number) {
    this.itemsPerPage = items
  }
  
  /** Load spellings from GraphQL with server-side pagination */
  async loadSpellings(): Promise<void> {
    // Prevent multiple simultaneous loads
    if (this._loadingSpellings) {
      console.log('loadSpellings already in progress, skipping')
      return
    }
    
    console.log('loadSpellings called')
    this._loadingSpellings = true
    this.loading = true
    this.error = ''
    this.notifyStateChange()
    
    try {
      console.log('Starting GraphQL query...')
      // Add timeout to prevent infinite loading
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Request timeout: Query took too long')), 30000) // 30 second timeout
      })

      const filter = this.filter.trim() || null
      const filterMode = this.filterMode === 'equals' ? 'EQUALS' : 'INCLUDES'
      const offset = (this.currentPage - 1) * this.itemsPerPage

      // Single query: backend handles finding words with matching spellings and returning all their spellings
      // When filter is provided: returns all spellings for words that match (complete word groups)
      // When no filter: returns paginated spellings normally
      const [countResult, spellingsResult] = await Promise.all([
        Promise.race([
          apolloClient.query<{ spellingsCount: number }>({
            query: gql`
              query GetSpellingsCount($filter: String, $filterMode: SpellingsFilterMode) {
                spellingsCount(filter: $filter, filterMode: $filterMode)
              }
            `,
            variables: { filter, filterMode },
            errorPolicy: 'all',
            fetchPolicy: 'network-only',
          }),
          timeoutPromise,
        ]),
        Promise.race([
          apolloClient.query<SpellingsData>({
            query: gql`
              query GetSpellings($limit: Int, $offset: Int, $filter: String, $filterMode: SpellingsFilterMode) {
                spellings(limit: $limit, offset: $offset, filter: $filter, filterMode: $filterMode) { 
                  id 
                  spelling 
                  system { id name } 
                  meaning { 
                    id 
                    language { id name }
                    word { id }
                    type { id name }
                  } 
                }
              }
            `,
            variables: {
              limit: this.itemsPerPage,
              offset,
              filter,
              filterMode,
            },
            errorPolicy: 'all',
            fetchPolicy: 'network-only',
          }),
          timeoutPromise,
        ]),
      ])

      // Check count result
      const countErrors = (countResult as any).errors as Array<{ message?: string }> | undefined
      if (countErrors && countErrors.length > 0) {
        console.error('GraphQL errors in count:', countErrors)
      }
      this.totalCount = (countResult as any).data?.spellingsCount ?? 0

      // Check spellings result
      const { data, error } = spellingsResult as any
      const errors = (spellingsResult as any).errors as Array<{ message?: string; path?: readonly (string | number)[]; extensions?: any }> | undefined
      
      console.log('Query result:', { data: data ? `data with ${data.spellings?.length ?? 0} items` : 'no data', error, errors, totalCount: this.totalCount })
      
      // Check for GraphQL errors
      if (errors && errors.length > 0) {
        const errorMessages = errors.map((e: { message?: string; path?: readonly (string | number)[]; extensions?: any }) => {
          const msg = e.message || 'Unknown error'
          const path = e.path ? ` at ${e.path.join('.')}` : ''
          const extensions = e.extensions ? ` (${JSON.stringify(e.extensions)})` : ''
          return `${msg}${path}${extensions}`
        }).join('; ')
        console.error('GraphQL errors:', errors)
        throw new Error(`GraphQL errors: ${errorMessages}`)
      }
      
      // Check for Apollo error
      if (error) {
        console.error('Apollo error:', error)
        const errorMessage = error.message || 'Unknown Apollo error'
        const networkError = (error as any).networkError
        if (networkError) {
          console.error('Network error:', networkError)
          throw new Error(`Network error: ${networkError.message || JSON.stringify(networkError)}`)
        }
        throw new Error(`Apollo error: ${errorMessage}`)
      }
      
      this.headwords = (spellingsResult as any).data?.spellings ?? []
      console.log(`Loaded ${this.headwords.length} spellings (page ${this.currentPage}, total: ${this.totalCount})`)
      // Clear caches when data changes
      this._groupedCache = null
      this._spellingSystemsCache.clear()
      
      // Process data asynchronously to avoid blocking UI
      this.processDataAsync()
    } catch (e) {
      console.error('Error loading spellings:', e)
      
      let errorMessage = 'Could not load words.'
      if (e instanceof Error) {
        errorMessage = e.message || e.toString()
        // Check for network error details
        const anyError = e as any
        if (anyError.networkError) {
          const netError = anyError.networkError
          errorMessage = `Network error: ${netError.message || netError.statusCode || JSON.stringify(netError)}`
        }
        if (anyError.graphQLErrors && anyError.graphQLErrors.length > 0) {
          errorMessage = `GraphQL errors: ${anyError.graphQLErrors.map((err: any) => err.message).join('; ')}`
        }
      } else if (typeof e === 'string') {
        errorMessage = e
      } else if (e && typeof e === 'object') {
        const errorObj = e as any
        errorMessage = errorObj.message || errorObj.error || errorObj.toString() || JSON.stringify(e)
      } else {
        errorMessage = String(e) || 'Unknown error occurred'
      }
      
      this.error = errorMessage
      this.headwords = []
      this.totalCount = 0
    } finally {
      this.loading = false
      this._loadingSpellings = false
      this.notifyStateChange()
    }
  }

  /** Load languages from GraphQL */
  async loadLanguages(): Promise<void> {
    try {
      const result = await apolloClient.query<LanguagesData>({
        query: gql`
          query GetLanguages {
            languages {
              id
              name
            }
          }
        `,
        errorPolicy: 'all',
        fetchPolicy: 'network-only',
      })

      // Apollo Client includes errors in result when errorPolicy: 'all' is used
      const errors = (result as any).errors as Array<{ message?: string }> | undefined
      if (errors && errors.length > 0) {
        console.error('GraphQL errors loading languages:', errors)
        return
      }

      this.languages = result.data?.languages ?? []
    } catch (e) {
      console.error('Error loading languages:', e)
      // Don't set error state - languages are optional for filtering
    }
  }

  /** Load meaning types from GraphQL */
  async loadMeaningTypes(): Promise<void> {
    try {
      const result = await apolloClient.query<MeaningTypesData>({
        query: gql`
          query GetMeaningTypes {
            meaningTypes {
              id
              name
            }
          }
        `,
        errorPolicy: 'all',
        fetchPolicy: 'network-only',
      })

      // Apollo Client includes errors in result when errorPolicy: 'all' is used
      const errors = (result as any).errors as Array<{ message?: string }> | undefined
      if (errors && errors.length > 0) {
        console.error('GraphQL errors loading meaning types:', errors)
        return
      }

      this.meaningTypes = result.data?.meaningTypes ?? []
    } catch (e) {
      console.error('Error loading meaning types:', e)
      // Don't set error state - meaning types are optional for filtering
    }
  }

  /** Process data asynchronously in chunks to avoid blocking UI */
  private async processDataAsync() {
    if (this._processingGrouped || this.headwords.length === 0) return
    
    this._processingGrouped = true
    this.processing = true
    this.notifyStateChange()
    
    // Use requestIdleCallback if available, otherwise use setTimeout
    const scheduleChunk = (callback: () => void) => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(callback, { timeout: 100 })
      } else {
        setTimeout(callback, 0)
      }
    }
    
    // Process grouping in chunks
    scheduleChunk(() => {
      this.computeGrouped()
      this._processingGrouped = false
      this.processing = false
      this.notifyStateChange()
    })
  }
  
  /** Compute grouped data (chunked processing) */
  private computeGrouped(): WordGroup[] {
    const cacheKey = `headwords:${this.headwords.length}`
    if (this._groupedCache && this._groupedCacheKey === cacheKey) {
      return this._groupedCache
    }
    
    const byWord = new Map<string, Spelling[]>()
    const chunkSize = 1000 // Process 1000 items at a time
    
    for (let i = 0; i < this.headwords.length; i += chunkSize) {
      const chunk = this.headwords.slice(i, i + chunkSize)
      for (const s of chunk) {
        const wordId = s.meaning?.word?.id ?? s.id
        if (!byWord.has(wordId)) byWord.set(wordId, [])
        byWord.get(wordId)!.push(s)
      }
    }
    
    this._groupedCache = Array.from(byWord.entries()).map(([wordId, spellings]) => ({
      wordId,
      spellings,
    }))
    this._groupedCacheKey = cacheKey
    return this._groupedCache
  }
  
  /** Groups spellings by word.id. Memoized for performance. */
  getGrouped(): WordGroup[] {
    // If processing is in progress, return cached or empty array
    if (this._processingGrouped && this._groupedCache) {
      return this._groupedCache
    }
    
    // Always check cache validity by calling computeGrouped
    // This ensures cache is invalidated when headwords change
    return this.computeGrouped()
  }

  /** Word groups filtered by search. With server-side filtering, this just returns grouped data. */
  getFiltered(): WordGroup[] {
    const grouped = this.getGrouped()
    const filterValue = this.filter.trim().toLowerCase()
    if (!filterValue) {
      return grouped
    }
    if (this.filterMode === 'includes') {
      // Server-side filtering already applied
      return grouped
    }
    // Equals: apply client-side exact match
    return grouped.filter(group =>
      group.spellings.some(spelling => spelling.spelling?.toLowerCase() === filterValue)
    )
  }

  /** Total number of pages for filtered results (based on total word count from server). */
  getTotalPages(): number {
    const filterValue = this.filter.trim()
    if (this.filterMode === 'equals' && filterValue) {
      return Math.ceil(this.getFiltered().length / this.itemsPerPage)
    }
    // totalCount represents the number of words (when filtered) or spellings (when not filtered)
    // Since we paginate by word, use totalCount for word pagination
    return Math.ceil(this.totalCount / this.itemsPerPage)
  }

  /** Paginated word groups. With server-side pagination, return all grouped words (already paginated). */
  getPaginated(): WordGroup[] {
    const filterValue = this.filter.trim()
    if (this.filterMode === 'equals' && filterValue) {
      return this.getFiltered()
    }
    // Server already paginated words, so return all grouped words we received
    return this.getGrouped()
  }

  /** Start index of current page (1-based). */
  getPageStart(): number {
    const filterValue = this.filter.trim()
    if (this.filterMode === 'equals' && filterValue) {
      const total = this.getFiltered().length
      if (total === 0) return 0
      return (this.currentPage - 1) * this.itemsPerPage + 1
    }
    if (this.totalCount === 0) return 0
    return (this.currentPage - 1) * this.itemsPerPage + 1
  }

  /** End index of current page (1-based). */
  getPageEnd(): number {
    const filterValue = this.filter.trim()
    if (this.filterMode === 'equals' && filterValue) {
      const filtered = this.getFiltered()
      const total = filtered.length
      const actualEnd = (this.currentPage - 1) * this.itemsPerPage + filtered.length
      return Math.min(actualEnd, total)
    }
    const grouped = this.getGrouped()
    const actualEnd = (this.currentPage - 1) * this.itemsPerPage + grouped.length
    return Math.min(actualEnd, this.totalCount)
  }

  /** Get available spelling systems for a language from the spellings data */
  getAvailableSpellingSystemsForLanguage(languageId: string): SpellingSystem[] {
    // Check cache first
    if (this._spellingSystemsCache.has(languageId)) {
      return this._spellingSystemsCache.get(languageId)!
    }
    
    const systemsMap = new Map<string, { id: string; name: string }>()
    
    // Find all spellings for this language
    for (const spelling of this.headwords) {
      if (spelling.meaning?.language?.id === languageId) {
        const systemId = spelling.system.id
        const systemName = spelling.system.name || `System ${systemId}`
        if (!systemsMap.has(systemId)) {
          systemsMap.set(systemId, { id: systemId, name: systemName })
        }
      }
    }
    
    const systems = Array.from(systemsMap.values()).sort((a, b) => a.id.localeCompare(b.id))
    this._spellingSystemsCache.set(languageId, systems)
    return systems
  }
  
  /** Clear all caches (useful when column configuration changes) */
  clearFilterCache() {
  }
  
  /** Clear all caches */
  clearAllCaches() {
    this._groupedCache = null
    this._spellingSystemsCache.clear()
    this._processingGrouped = false
  }
}
